import { type Automation, type FlowStep, type TriggerStep } from '@spike/automation-model';
import { type GeneratedAutomation, type GeneratedFlowStep } from '@spike/automations-action';
import {
    uid,
    countType,
    formatDelayLabel,
    formatSmsSubtitle,
    formatEmailSubtitle,
    DEFAULT_UNSUBSCRIBE_MESSAGE,
    DEFAULT_SENDER_NAME,
    DEFAULT_SENDER_EMAIL
} from '../Canvas/useAutomationFlow';

export interface MapGeneratedAutomationResult {
    automation: Automation;
    warnings: string[];
}

/**
 * Hydrates the simplified /automations/generate response (steps carry only their
 * essential content, no id/label/subtitle) into the full Automation/FlowStep shape
 * the canvas and manual builder expect — reusing the same defaults and label
 * formatting the manual builder applies to hand-inserted steps.
 */
export function mapGeneratedAutomation(generated: GeneratedAutomation, name: string): MapGeneratedAutomationResult {
    const warnings: string[] = [];
    const steps: FlowStep[] = [];

    for (const step of generated.steps) {
        // The union says this can't happen, but the payload comes from an LLM:
        // a `split` or a malformed entry does reach here at runtime. Widening
        // for the guard keeps the check meaningful instead of narrowing to
        // `never` and erroring on step.type.
        const candidate = step as GeneratedFlowStep | { type?: string } | null;
        const type = candidate?.type;

        if (type !== 'delay' && type !== 'sms' && type !== 'email') {
            warnings.push(
                type
                    ? `Skipped an unsupported generated step of type "${type}".`
                    : 'Skipped an unsupported generated step.'
            );
            continue;
        }

        switch (step.type) {
            case 'delay':
                steps.push({
                    id: uid(),
                    type: 'delay',
                    label: formatDelayLabel(step.amount, step.unit),
                    subtitle: '',
                    amount: step.amount,
                    unit: step.unit,
                    extraMinutes: 0
                });
                break;

            case 'sms': {
                const smsName = `SMS #${countType(steps, 'sms') + 1}`;
                steps.push({
                    id: uid(),
                    type: 'sms',
                    label: smsName,
                    subtitle: formatSmsSubtitle(step.message),
                    smsName,
                    content: {
                        message: step.message,
                        unsubscribeMessage: step.unsubscribeMessage || DEFAULT_UNSUBSCRIBE_MESSAGE
                    },
                    skipCampaign: true,
                    skipFlow: true,
                    dayOffset: 0
                });
                break;
            }

            case 'email': {
                const emailName = `Email #${countType(steps, 'email') + 1}`;
                steps.push({
                    id: uid(),
                    type: 'email',
                    label: emailName,
                    subtitle: formatEmailSubtitle(step.subject),
                    emailName,
                    subjectLine: step.subject,
                    previewText: '',
                    senderName: DEFAULT_SENDER_NAME,
                    senderEmail: DEFAULT_SENDER_EMAIL,
                    useAsReplyTo: true,
                    skipRecentlyEmailed: true,
                    dayOffset: 0
                });
                break;
            }
        }
    }

    const triggerStep: TriggerStep = {
        id: 'trigger-1',
        type: 'trigger',
        label: 'Trigger',
        subtitle: generated.trigger.kind === 'filter' ? 'Scheduled' : 'Action'
    };

    const now = new Date().toISOString();

    const automation: Automation = {
        id: '',
        name,
        status: 'draft',
        trigger: generated.trigger,
        steps: [triggerStep, ...steps],
        createdAt: now,
        updatedAt: now
    };

    return { automation, warnings };
}
